// =============================================================================
// PTEL - ModeratorPanel
// Moderator Opción B · Paso 3
// Vista reducida: solo solicitudes pendientes (sin audit log, sin usuarios)
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useAudit } from '@/hooks/useAudit';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsMounted } from '@/hooks/useIsMounted';
import {
  listPendingRequests,
  approveRequest,
  rejectRequest,
} from '@/services/firebase/editorRequest.service';
import { PendingRequests } from './PendingRequests';
import type { EditorRequest, RoleDuration } from '@/types/roles';
import type { Theme } from '@/components/auth/auth.types';
import { getAuthTokens, type AuthTokenSet } from '@/components/auth/auth.tokens';

// SEC0-b2c DOP-6 (a′): el moderador no concede «Indefinido» (lo comprueban también las reglas).
const MODERATOR_DURATIONS: readonly RoleDuration[] = ['15d', '3m', '1y'];

// -----------------------------------------------------------------------------
// PANADM-b2 DOP-6 (a): mismo aviso de fallo que `AdminPanel` (PANADM-b1):
// motivo, código y «Reintentar»; «No hay solicitudes» solo si la lectura fue bien.
// -----------------------------------------------------------------------------

interface LoadError {
  message: string;
  code: string;
}

/** El código de Firebase va aparte; el texto es el propio de la lista, en español. */
function toLoadError(err: unknown, message: string): LoadError {
  const code = (err as { code?: unknown } | null)?.code;
  return { message, code: typeof code === 'string' && code ? code : 'unknown' };
}

function LoadErrorNotice({ testId, error, what, onRetry, t }: {
  testId: string; error: LoadError; what: string; onRetry: () => void; t: AuthTokenSet;
}) {
  return (
    <div data-testid={testId} role="alert" style={{
      padding: '12px', background: t.errorBg,
      border: `1px solid ${t.error}30`, borderRadius: '8px',
      color: t.error, fontSize: '13px', marginBottom: '12px',
    }}>
      <div>
        {error.message} Motivo: <span data-testid={`${testId}-code`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{error.code}</span>.
        {' '}No significa que no haya {what}: no se ha podido leer.
      </div>
      <button
        type="button"
        data-testid={`${testId}-retry`}
        onClick={onRetry}
        style={{
          marginTop: '8px', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
          background: 'transparent', border: `1px solid ${t.error}`, color: t.error,
          fontSize: '12px', fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        Reintentar
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ModeratorPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme?: Theme;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ModeratorPanel({
  open,
  onOpenChange,
  theme = 'dark',
}: ModeratorPanelProps) {
  const t = getAuthTokens(theme);
  const { userData } = useAuth();
  const { logDocument } = useAudit();
  const { canModerate } = usePermissions();

  // SSCA-FIX-1: guard de montaje para los setState tras await de refresh.
  const isMounted = useIsMounted();

  const [requests, setRequests] = useState<EditorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<LoadError | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setFailed(null);
    try {
      const pending = await listPendingRequests();
      if (isMounted()) setRequests(pending);
    } catch (err) {
      // PANADM-b2: el fallo se dice como fallo, con su código; no se deja la lista vieja.
      if (isMounted()) {
        setRequests([]);
        setFailed(toLoadError(err, 'No se han podido leer las solicitudes de editor.'));
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  // Solo cargar datos si el usuario tiene permiso (evita filtración de datos)
  useEffect(() => {
    if (open && canModerate) void refresh();
  }, [open, canModerate, refresh]);

  // ---------------------------------------------------------------------------
  // Guard de acceso (moderator + admin)
  // ---------------------------------------------------------------------------

  if (!canModerate) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" style={{ background: t.bgCard, color: t.text }}>
          <SheetHeader>
            <SheetTitle style={{ color: t.text }}>Acceso denegado</SheetTitle>
            <SheetDescription style={{ color: t.textSecondary }}>
              Solo los moderadores pueden acceder a este panel.
            </SheetDescription>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleApprove = async (requestId: string, duration: RoleDuration) => {
    if (!userData?.uid) return;
    await approveRequest(requestId, userData.uid, duration);
    await logDocument('approve', requestId, '', null, {
      duration,
      by: 'moderator',
    });
    await refresh();
  };

  const handleReject = async (requestId: string, reason?: string) => {
    if (!userData?.uid) return;
    await rejectRequest(requestId, userData.uid, reason);
    await logDocument('reject', requestId, '', null, {
      reason: reason ?? 'sin motivo',
      by: 'moderator',
    });
    await refresh();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        style={{
          background: t.bgCard,
          color: t.text,
          width: '480px',
          maxWidth: '92vw',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SheetHeader style={{ padding: '16px 16px 0' }}>
          <SheetTitle
            style={{ color: t.text, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Panel de moderación
          </SheetTitle>
          <SheetDescription style={{ color: t.textSecondary }}>
            Gestión de solicitudes de rol Editor
          </SheetDescription>
        </SheetHeader>

        <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
          {failed && (
            <LoadErrorNotice
              testId="mod-error"
              error={failed}
              what="solicitudes"
              onRetry={() => void refresh()}
              t={t}
            />
          )}
          <PendingRequests
            requests={requests}
            loading={loading}
            onApprove={handleApprove}
            onReject={handleReject}
            t={t}
            durations={MODERATOR_DURATIONS}
          />

          {!loading && !failed && requests.length === 0 && (
            <div
              data-testid="mod-empty"
              style={{
                textAlign: 'center',
                padding: '40px',
                color: t.textMuted,
                fontSize: '13px',
              }}
            >
              No hay solicitudes pendientes
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
